/**
 * Crypto E2E — zero-knowledge.
 * AES-GCM 256 + PBKDF2 310k iterations SHA-256.
 * Ciphertext layout: salt(16) || iv(12) || ciphertext || tag
 */

const PBKDF2_ITERATIONS = 310_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;

async function deriveKey(
  password: string,
  salt: BufferSource
): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptJSON(
  data: unknown,
  password: string
): Promise<string> {
  const enc = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveKey(password, salt);
  const plaintext = enc.encode(JSON.stringify(data));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    plaintext
  );
  const combined = new Uint8Array(
    SALT_BYTES + IV_BYTES + ct.byteLength
  );
  combined.set(salt, 0);
  combined.set(iv, SALT_BYTES);
  combined.set(new Uint8Array(ct), SALT_BYTES + IV_BYTES);
  return toBase64(combined);
}

export async function decryptJSON<T>(
  base64: string,
  password: string
): Promise<T> {
  const raw = fromBase64(base64);
  const salt = raw.slice(0, SALT_BYTES);
  const iv = raw.slice(SALT_BYTES, SALT_BYTES + IV_BYTES);
  const ct = raw.slice(SALT_BYTES + IV_BYTES);
  const key = await deriveKey(password, salt);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ct
  );
  return JSON.parse(new TextDecoder().decode(pt)) as T;
}

// ---- v2: clave por cuenta (contrato docs/contracts/embudo-v1.md §1) ----

const V2_PREFIX = 'v2.';

export class LegacyPayloadError extends Error {
  constructor() {
    super('Payload v1: hace falta la password para descifrarlo');
    this.name = 'LegacyPayloadError';
  }
}

export async function deriveAccountKey(password: string, code: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', enc.encode('kairos-gtd/v2/' + code))
  );
  const salt = new Uint8Array(new ArrayBuffer(SALT_BYTES));
  salt.set(digest.slice(0, SALT_BYTES));
  return deriveKey(password, salt);
}

export async function encryptV2(
  data: unknown,
  key: CryptoKey,
  iv?: Uint8Array<ArrayBuffer>
): Promise<string> {
  const ivBytes = iv ?? crypto.getRandomValues(new Uint8Array(new ArrayBuffer(IV_BYTES)));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: ivBytes },
      key,
      new TextEncoder().encode(JSON.stringify(data))
    )
  );
  const out = new Uint8Array(IV_BYTES + ct.byteLength);
  out.set(ivBytes, 0);
  out.set(ct, IV_BYTES);
  return V2_PREFIX + toBase64(out);
}

export async function decryptV2<T>(payload: string, key: CryptoKey): Promise<T> {
  if (!payload.startsWith(V2_PREFIX)) throw new Error('No es payload v2');
  const raw = fromBase64(payload.slice(V2_PREFIX.length));
  const iv = raw.slice(0, IV_BYTES);
  const ct = raw.slice(IV_BYTES);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(pt)) as T;
}

export function isV2(payload: string): boolean {
  return payload.startsWith(V2_PREFIX);
}

export async function decryptAny<T>(
  payload: string,
  key: CryptoKey,
  password?: string
): Promise<T> {
  if (isV2(payload)) return decryptV2<T>(payload, key);
  if (!password) throw new LegacyPayloadError();
  return decryptJSON<T>(payload, password);
}

/**
 * Password hash para almacenar en local (solo check, no para derivar key).
 * Usamos SHA-256 del password + sal fija del deviceId para evitar guardar la pass plana.
 */
export async function hashPassword(
  password: string,
  deviceId: string
): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest(
    'SHA-256',
    enc.encode(deviceId + ':' + password)
  );
  return toBase64(new Uint8Array(buf));
}

export function genDeviceId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return toBase64Url(bytes);
}

// ---- base64 helpers ----

function toBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function fromBase64(str: string): Uint8Array<ArrayBuffer> {
  const bin = atob(str);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function toBase64Url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
